import { Panel, Field, FieldList, Empty, Chip } from "./Panel";
import { parseSpecializations } from "../helpers";
import { formatDateTimeBs } from "@/lib/format";
import { SESSION_MODALITY_LABELS_BS } from "@/lib/modality";
import type { Therapist } from "@/types";

function DateValue({ value }: { value: string | null | undefined }) {
  if (!value) return <Empty>nije zabilježeno</Empty>;
  return <span className="tabular-nums">{formatDateTimeBs(value)}</span>;
}

/**
 * Identity, credentials and working mode: the facts a decision rests on.
 * Languages and matching tags are deliberately absent, they live in their
 * editors (Oznake, Matching profil) so no fact appears on the page twice.
 */
export function TherapistIdentityPanel({ therapist }: { therapist: Therapist }) {
  const specializations = parseSpecializations(therapist.specializations);

  return (
    <Panel title="Podaci">
      <FieldList>
        <Field label="Email">
          <a
            href={`mailto:${therapist.userEmail}`}
            className="break-all text-primary hover:underline"
          >
            {therapist.userEmail}
          </a>
        </Field>
        <Field label="Telefon">
          {therapist.phone ? (
            <a href={`tel:${therapist.phone}`} className="text-primary hover:underline">
              {therapist.phone}
            </a>
          ) : (
            <Empty />
          )}
        </Field>
        <Field label="Broj licence" mono>
          {therapist.licenseNumber || <Empty />}
        </Field>
        <Field label="Iskustvo" mono>
          {therapist.yearsExperience} god.
        </Field>
        <Field label="Način rada">
          {therapist.sessionModality ? (
            SESSION_MODALITY_LABELS_BS[therapist.sessionModality]
          ) : (
            <Empty />
          )}
        </Field>
        <Field label="Ordinacija">
          {therapist.officeAddress || <Empty>nije uneseno</Empty>}
        </Field>
        <Field label="Specijalizacije">
          <div className="flex flex-wrap gap-1">
            {specializations.length > 0 ? (
              specializations.map((s) => (
                <Chip key={s} tone="brand">
                  {s}
                </Chip>
              ))
            ) : (
              <Empty />
            )}
          </div>
        </Field>
      </FieldList>
    </Panel>
  );
}

/**
 * Billing identity. Which set of fields matters depends on how the therapist
 * is paid, so only the relevant group is shown rather than a wall of blanks.
 * The payout method itself is chosen in the Oznake editor below this panel.
 */
export function TherapistBillingPanel({ therapist }: { therapist: Therapist }) {
  const viaInvoice = therapist.payoutMethod === "invoice";

  return (
    <Panel title="Naplata">
      <FieldList>
        {viaInvoice ? (
          <>
            <Field label="Naziv firme">{therapist.businessName || <Empty />}</Field>
            <Field label="Adresa firme">{therapist.businessAddress || <Empty />}</Field>
            <Field label="ID broj" mono>
              {therapist.businessTaxId || <Empty />}
            </Field>
            <Field label="PDV broj" mono>
              {therapist.businessVatId || <Empty>nije PDV obveznik</Empty>}
            </Field>
          </>
        ) : (
          <>
            <Field label="Adresa">{therapist.personalAddress || <Empty />}</Field>
            <Field label="JMBG" mono>
              {therapist.personalTaxId || <Empty />}
            </Field>
          </>
        )}
        {/* Račun za isplatu važi za oba načina isplate. */}
        <Field label="Banka">{therapist.bankName || <Empty />}</Field>
        <Field label="Broj računa" mono>
          {therapist.bankAccount || <Empty />}
        </Field>
      </FieldList>
    </Panel>
  );
}

/** Account history, so an admin can see what changed and when. */
export function TherapistTimelinePanel({ therapist }: { therapist: Therapist }) {
  return (
    <Panel title="Historija naloga">
      <FieldList>
        <Field label="Prijava">
          <DateValue value={therapist.createdAt} />
        </Field>
        <Field label="Odobren">
          <DateValue value={therapist.approvedAt} />
        </Field>
        {therapist.deactivatedAt && (
          <Field label="Deaktiviran">
            <DateValue value={therapist.deactivatedAt} />
          </Field>
        )}
        <Field label="Zadnja izmjena">
          <DateValue value={therapist.updatedAt} />
        </Field>
      </FieldList>

      {therapist.status === "rejected" && therapist.rejectionReason && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/60 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-rose-700">
            Razlog odbijanja
          </p>
          <p className="mt-1 text-sm leading-relaxed text-rose-700/90">
            {therapist.rejectionReason}
          </p>
        </div>
      )}

      {therapist.deactivatedAt && therapist.deactivationReason && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-amber-700">
            Razlog deaktivacije
          </p>
          <p className="mt-1 text-sm leading-relaxed text-amber-700/90">
            {therapist.deactivationReason}
          </p>
        </div>
      )}
    </Panel>
  );
}

export function TherapistBioPanel({ bio }: { bio: string }) {
  return (
    <Panel title="Biografija">
      {bio?.trim() ? (
        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">
          {bio}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground/60">
          Terapeut još nije napisao biografiju.
        </p>
      )}
    </Panel>
  );
}
